/**
 * kelly-0915-editwheel-description-editor-copies-asterisks-ux (v2).
 *
 * WHY THIS EXISTS -- the v1 fix (#5570) missed the live path.
 *
 * #5570 hung `convertMarkdownEmphasisToHtml` off Froala's own
 * `paste.afterCleanup` lifecycle hook. That hook only runs when Froala's
 * NATIVE paste pipeline runs. It never does for the wheel Description field,
 * because #5586 (`allowRichPaste`) landed a handler that calls
 * `pasteEvent.preventDefault()` and inserts the payload itself. Measured on
 * https://web-dev.iyuga.net/builder/6367, bundle main.5e7a2989.js,
 * 2026-09-15: the paste event on `.fr-element` came back
 * `defaultPrevented=true`, editor HTML after a real OS-clipboard Ctrl+V was
 * `<p>Kelly test **bold words** and *slanted words* end</p>` -- 6 literal
 * asterisks, 0 `<strong>`, 0 `<em>` -- and /wheel/6367 rendered the same 6
 * asterisks. Evidence: C:\iYuga\qa-notes\vy\deploy30-proof-0915\READY.md row
 * #5570.
 *
 * So the markdown conversion has to happen INSIDE the rich-paste payload
 * that #5586 inserts, not in a Froala hook that path bypasses. This module
 * is that single payload builder: sanitize (or paragraph-wrap) exactly as
 * #5586 already does, then convert markdown list markers and markdown
 * emphasis into real HTML.
 *
 * SECURITY: the markdown conversion only ever rewrites DOM TEXT NODES, and
 * always inserts captured content through `.textContent`, so it cannot
 * introduce markup. It also runs AFTER `sanitizePastedHtml`'s allowlist, so
 * #5586's sanitisation is never weakened or re-ordered around.
 *
 * STRAY ASTERISKS ARE KEPT ON PURPOSE. Only *paired* markdown emphasis is
 * converted; a lone `*` ("3 * 4 = 12") stays literal, because deleting
 * characters a user actually typed is a worse bug than leaving one asterisk.
 */
import {
  plainTextToParagraphHtml,
  sanitizePastedHtml,
} from "../FroalaHelpers";
import { convertMarkdownEmphasisToHtml } from "./markdownEmphasisToHtml";

// A markdown bullet: line-leading "-", "*" or "+" followed by whitespace.
// The trailing whitespace requirement is what keeps "*italic*" (no space
// after the marker) from being misread as a bullet.
const BULLET_RE = /^\s*[-*+][ \t]+/;

function isBulletParagraph(node) {
  if (!node || node.nodeType !== 1 || node.tagName !== "P") return false;
  const first = node.firstChild;
  if (!first || first.nodeType !== 3) return false;
  return BULLET_RE.test(first.nodeValue);
}

function paragraphToListItem(doc, paragraph) {
  const li = doc.createElement("li");
  Array.from(paragraph.childNodes).forEach((child, index) => {
    const moved = child.cloneNode(true);
    if (index === 0 && moved.nodeType === 3) {
      moved.nodeValue = moved.nodeValue.replace(BULLET_RE, "");
    }
    li.appendChild(moved);
  });
  return li;
}

/**
 * Turn runs of consecutive bullet-marker paragraphs into a real `<ul>`.
 * A pasted markdown list arrives from `plainTextToParagraphHtml` as one
 * `<p>` per line, so grouping consecutive bullet paragraphs is exactly the
 * list the user copied.
 *
 * @param {string} html
 * @param {Document} [doc]
 * @returns {string}
 */
export function convertMarkdownListsToHtml(
  html,
  doc = typeof document !== "undefined" ? document : undefined
) {
  if (typeof html !== "string" || !html || !doc) return html;

  const container = doc.createElement("div");
  container.innerHTML = html;

  let index = 0;
  const children = () => Array.from(container.childNodes);

  while (index < children().length) {
    const nodes = children();
    if (!isBulletParagraph(nodes[index])) {
      index += 1;
      continue;
    }

    const run = [];
    let cursor = index;
    while (cursor < nodes.length && isBulletParagraph(nodes[cursor])) {
      run.push(nodes[cursor]);
      cursor += 1;
    }

    const list = doc.createElement("ul");
    run.forEach((paragraph) => list.appendChild(paragraphToListItem(doc, paragraph)));
    container.insertBefore(list, run[0]);
    run.forEach((paragraph) => paragraph.remove());
    index += 1;
  }

  return container.innerHTML;
}

/**
 * Build the HTML `allowRichPaste` inserts for one paste.
 *
 * @param {{ html?: string, text?: string }} clipboard - the `text/html` and
 *   `text/plain` flavours read off the clipboard, in that preference order
 *   (unchanged from #5586).
 * @param {Document} [doc]
 * @returns {string}
 */
export function buildRichPastePayload({ html, text } = {}, doc) {
  const base = html ? sanitizePastedHtml(html) : plainTextToParagraphHtml(text);
  if (!base) return base;
  return convertMarkdownEmphasisToHtml(convertMarkdownListsToHtml(base, doc), doc);
}

export default buildRichPastePayload;
